import React, { useRef, useState, useMemo } from 'react';
import {
  downloadProductsBulkEditSheet,
  analyzeProductsBulkEdit,
  executeProductsBulkEdit,
  summarizeBulkEditResult,
  buildBulkEditHeaders,
} from '../../utils/productsBulkEditExcel';
import ProductsBulkEditPreviewModal from './ProductsBulkEditPreviewModal';

const ProductsBulkEditModal = ({
  isOpen,
  onClose,
  products = [],
  setToast,
  onUpdated,
  moduleOptions = {},
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewState, setPreviewState] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const opts = useMemo(
    () => ({
      insumoEnabled: Boolean(moduleOptions.insumoEnabled),
      ventasEnabled: Boolean(moduleOptions.ventasEnabled),
      produccionEnabled: Boolean(moduleOptions.produccionEnabled),
      machineryEnabled: Boolean(moduleOptions.machineryEnabled),
      warehouseVisibilityEnabled: Boolean(moduleOptions.warehouseVisibilityEnabled),
    }),
    [moduleOptions],
  );

  const columnSummary = useMemo(() => {
    return buildBulkEditHeaders(opts).slice(2).join(' · ');
  }, [opts]);

  const handleClose = () => {
    if (busy || confirming) return;
    setPreviewOpen(false);
    setPreviewState(null);
    onClose();
  };

  const handleDownload = async () => {
    try {
      setBusy(true);
      await downloadProductsBulkEditSheet(products, opts);
      setToast({
        isOpen: true,
        message: `Planilla descargada con ${products.length} producto(s) y catálogo de unidades.`,
        type: 'success',
      });
    } catch {
      setToast({
        isOpen: true,
        message: 'No se pudo generar la planilla Excel.',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setBusy(true);
    try {
      const analysis = await analyzeProductsBulkEdit(file, opts);
      if (!analysis.success) {
        setToast({
          isOpen: true,
          message: analysis.fatalMessage || 'No se pudo leer el archivo.',
          type: 'error',
        });
        return;
      }
      setPreviewState(analysis);
      setPreviewOpen(true);
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'No se pudo leer el archivo. Use la planilla .xlsx descargada.',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewState?.success) return;

    setConfirming(true);
    try {
      const exec = await executeProductsBulkEdit(previewState.validTasks);
      const message = summarizeBulkEditResult(exec);
      const hasErr = exec.errors?.length > 0;
      const type = exec.updated > 0 && !hasErr ? 'success' : hasErr ? 'warning' : 'info';

      setToast({ isOpen: true, message, type });
      setPreviewOpen(false);
      setPreviewState(null);
      if (exec.updated > 0) {
        onUpdated?.();
        onClose();
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al aplicar la edición masiva.',
        type: 'error',
      });
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget && !busy && !confirming) handleClose();
        }}
        role="presentation"
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="products-bulk-edit-title"
        >
          <div className="p-6">
            <h3 id="products-bulk-edit-title" className="text-lg font-semibold text-gray-900">
              Edición masiva de productos
            </h3>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              Herramienta exclusiva del <strong>super administrador</strong> para corregir unidades y otros
              datos en lote. Descargue la planilla, edite las filas necesarias y súbala de nuevo.
            </p>

            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              La <strong>unidad de medida</strong> debe coincidir con un código activo del catálogo (hoja
              &quot;Unidades&quot;). Cambiar la unidad con kardex existente no convierte el historial.
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Columnas según módulos activos: {columnSummary}
            </p>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleDownload}
                disabled={busy || confirming || products.length === 0}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-teal-800 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100 disabled:opacity-50"
              >
                Descargar planilla ({products.length})
              </button>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy || confirming}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
              >
                {busy ? 'Leyendo…' : 'Subir planilla editada'}
              </button>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={onFileChange}
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={busy || confirming}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      <ProductsBulkEditPreviewModal
        isOpen={previewOpen}
        onClose={() => {
          if (!confirming) {
            setPreviewOpen(false);
            setPreviewState(null);
          }
        }}
        preview={previewState}
        onConfirm={handleConfirm}
        confirming={confirming}
      />
    </>
  );
};

export default ProductsBulkEditModal;
